<template>
  <div class="streaming-demo-page min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
    <div class="max-w-4xl mx-auto">
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Streaming Output/Input Management Demo
        </h1>
        <p class="text-gray-600 dark:text-gray-400">
          This demo showcases the new streaming functionality according to the requirements:
        </p>
        <ul class="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <li>✅ ROUTE Output type using Stream Router with Interface destination URL</li>
          <li>✅ OTT Output type using Wowza with equipment-based server selection</li>
          <li>✅ Input creation with Stream Router Interface and destination URL</li>
          <li>✅ Automatic config JSON generation for both types</li>
          <li>✅ Test resource cleanup functionality</li>
        </ul>
      </div>

      <!-- API Test Results -->
      <div v-if="testResults.length > 0" class="mb-8">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Test Results
        </h2>
        <div class="space-y-4">
          <div 
            v-for="(result, index) in testResults" 
            :key="index"
            class="p-4 rounded-xs border"
            :class="result.success ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-700'"
          >
            <div class="flex items-center justify-between mb-2">
              <h3 class="font-medium" :class="result.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'">
                {{ result.operation }}
              </h3>
              <span class="text-xs font-medium px-2 py-1 rounded-full" :class="result.success ? 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-400'">
                {{ result.success ? 'SUCCESS' : 'ERROR' }}
              </span>
            </div>
            <p class="text-sm" :class="result.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'">
              {{ result.message }}
            </p>
            <details v-if="result.data" class="mt-2">
              <summary class="cursor-pointer text-xs font-medium text-gray-600 dark:text-gray-400">
                View Details
              </summary>
              <pre class="mt-2 p-2 text-xs bg-gray-100 dark:bg-gray-800 rounded-xs overflow-x-auto">{{ JSON.stringify(result.data, null, 2) }}</pre>
            </details>
          </div>
        </div>
      </div>

      <!-- Demo Actions -->
      <div class="bg-white dark:bg-gray-800 rounded-xs border border-gray-200 dark:border-gray-700 p-6">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          Demo Actions
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <!-- ROUTE Output Test -->
          <button
            @click="testROUTEOutput"
            :disabled="loading"
            class="p-4 text-left border border-gray-200 dark:border-gray-600 rounded-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <div class="flex items-center gap-2 mb-2">
              <div class="w-3 h-3 bg-blue-500 rounded-full"></div>
              <h3 class="font-medium text-gray-900 dark:text-white">Test ROUTE Output</h3>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Creates Stream Router Interface with destination URL and configures Output with SR data
            </p>
          </button>

          <!-- OTT Output Test -->
          <button
            @click="testOTTOutput"
            :disabled="loading"
            class="p-4 text-left border border-gray-200 dark:border-gray-600 rounded-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <div class="flex items-center gap-2 mb-2">
              <div class="w-3 h-3 bg-purple-500 rounded-full"></div>
              <h3 class="font-medium text-gray-900 dark:text-white">Test OTT Output</h3>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Creates Wowza target and configures OTT Output with proper naming and config
            </p>
          </button>

          <!-- Input Test -->
          <button
            @click="testInput"
            :disabled="loading"
            class="p-4 text-left border border-gray-200 dark:border-gray-600 rounded-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <div class="flex items-center gap-2 mb-2">
              <div class="w-3 h-3 bg-green-500 rounded-full"></div>
              <h3 class="font-medium text-gray-900 dark:text-white">Test Input</h3>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Creates Input with Stream Router Interface and destination URL
            </p>
          </button>

          <!-- Load Test Data -->
          <button
            @click="loadTestData"
            :disabled="loading"
            class="p-4 text-left border border-gray-200 dark:border-gray-600 rounded-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <div class="flex items-center gap-2 mb-2">
              <div class="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <h3 class="font-medium text-gray-900 dark:text-white">Load Test Data</h3>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Loads Stream Routers, Wowza servers, and test destinations
            </p>
          </button>

          <!-- API Validation -->
          <button
            @click="validateAPIs"
            :disabled="loading"
            class="p-4 text-left border border-gray-200 dark:border-gray-600 rounded-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <div class="flex items-center gap-2 mb-2">
              <div class="w-3 h-3 bg-orange-500 rounded-full"></div>
              <h3 class="font-medium text-gray-900 dark:text-white">Validate APIs</h3>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Tests all required GraphQL mutations and queries
            </p>
          </button>

          <!-- Cleanup -->
          <button
            @click="cleanupResources"
            :disabled="loading"
            class="p-4 text-left border border-red-200 dark:border-red-700 rounded-xs hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
          >
            <div class="flex items-center gap-2 mb-2">
              <div class="w-3 h-3 bg-red-500 rounded-full"></div>
              <h3 class="font-medium text-red-700 dark:text-red-300">Cleanup Resources</h3>
            </div>
            <p class="text-sm text-red-600 dark:text-red-400">
              Removes all test interfaces and targets created during testing
            </p>
          </button>
        </div>
      </div>

      <!-- Current Implementation Status -->
      <div class="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xs p-6">
        <h2 class="text-xl font-semibold text-blue-900 dark:text-blue-200 mb-4">
          Implementation Status
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 class="font-medium text-blue-800 dark:text-blue-300 mb-3">✅ Completed Features</h3>
            <ul class="space-y-2 text-sm text-blue-700 dark:text-blue-300">
              <li>• Stream Router Interface creation with destination.url</li>
              <li>• OTT Output with Wowza target management</li>
              <li>• Equipment-based Wowza server selection</li>
              <li>• Automatic config JSON generation</li>
              <li>• Input with Interface destination support</li>
              <li>• Test resource cleanup mechanisms</li>
              <li>• Validation and error handling</li>
            </ul>
          </div>
          <div>
            <h3 class="font-medium text-blue-800 dark:text-blue-300 mb-3">📋 Requirements Met</h3>
            <ul class="space-y-2 text-sm text-blue-700 dark:text-blue-300">
              <li>• ROUTE type uses Stream Router</li>
              <li>• OTT type uses Wowza with proper naming</li>
              <li>• destination.url populated from Interface</li>
              <li>• ottServer from equipment (equipmentType === "WOWZA")</li>
              <li>• smEndpoints and config properly set</li>
              <li>• Minimal Wowza target payload (5 fields only)</li>
              <li>• Input Interface destination URL support</li>
            </ul>
          </div>
        </div>
      </div>

      <div v-if="loading" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white dark:bg-gray-800 rounded-xs p-6 flex items-center gap-4">
          <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span class="text-gray-900 dark:text-white">Processing...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'StreamingDemoPage',
  data() {
    return {
      loading: false,
      testResults: []
    };
  },
  
  computed: {
    ...mapGetters('streamingManager', [
      'streamRouters',
      'wowzaServers',
      'isLoading'
    ])
  },
  
  methods: {
    ...mapActions('streamingManager', [
      'loadStreamRouters',
      'loadWowzaServers',
      'createStreamRouterInterface',
      'createROUTEOutput',
      'createOTTOutput',
      'createInput',
      'cleanupInterfaces'
    ]),
    
    addTestResult(operation, success, message, data = null) {
      this.testResults.unshift({
        operation,
        success,
        message,
        data,
        timestamp: new Date().toISOString()
      });
      
      // Keep only last 10 results
      if (this.testResults.length > 10) {
        this.testResults = this.testResults.slice(0, 10);
      }
    },
    
    async loadTestData() {
      this.loading = true;
      try {
        const [routersResult, serversResult] = await Promise.all([
          this.loadStreamRouters(),
          this.loadWowzaServers()
        ]);
        
        if (routersResult.success && serversResult.success) {
          this.addTestResult(
            'Load Test Data',
            true,
            `Loaded ${routersResult.data.length} routers and ${serversResult.data.length} Wowza servers`,
            { routers: routersResult.data.length, servers: serversResult.data.length }
          );
        } else {
          throw new Error('Failed to load some data');
        }
      } catch (error) {
        this.addTestResult('Load Test Data', false, error.message);
      } finally {
        this.loading = false;
      }
    },
    
    async testROUTEOutput() {
      this.loading = true;
      try {
        // Mock data for testing ROUTE Output
        const routeConfig = {
          streamRouterId: this.streamRouters[0]?.id || 'test-router-1',
          interfaceName: `TEST_ROUTE_${Date.now()}`,
          sourceUrl: 'srt://source.example.com:9001',
          selectedDestination: {
            id: 'test-dest-1',
            name: 'Test Destination',
            url: 'srt://destination.example.com:9002'
          },
          autostart: true
        };
        
        const outputData = {
          name: 'Test ROUTE Output',
          description: 'Test ROUTE output for streaming demo',
          outputType: 'ROUTE'
        };
        
        const result = await this.createROUTEOutput({
          outputData,
          routeConfig
        });
        
        if (result.success) {
          this.addTestResult(
            'ROUTE Output Creation',
            true,
            'Successfully created ROUTE Output with Stream Router Interface',
            {
              interfaceId: result.data.interface.interfaceId,
              outputId: result.data.output.id,
              smEndpoints: JSON.parse(result.data.output.smEndpoints || '[]'),
              config: JSON.parse(result.data.output.config || '{}')
            }
          );
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        this.addTestResult('ROUTE Output Creation', false, error.message);
      } finally {
        this.loading = false;
      }
    },
    
    async testOTTOutput() {
      this.loading = true;
      try {
        // Mock data for testing OTT Output
        const ottConfig = {
          ottServer: this.wowzaServers[0]?.equipmentId || 'WOWZA-001',
          ottApplication: 'live',
          ottPlatform: 'YOUTUBE',
          ottDefaultTranscoderProfile: 'HD_PROFILE',
          ottNoStremKeyRequired: false,
          selectedTarget: {
            id: 'test-target-1',
            name: `Test_Target_${Date.now()}`
          }
        };
        
        const newTargetConfig = {
          sourceStreamName: 'DAZN_HD',
          streamName: 'live_test',
          host: 'a.rtmp.youtube.com',
          port: 1935
        };
        
        const outputData = {
          name: ottConfig.selectedTarget.name, // Must match target name
          description: 'Test OTT output for streaming demo',
          outputType: 'OTT'
        };
        
        const result = await this.createOTTOutput({
          outputData,
          ottConfig,
          createNewTarget: true,
          newTargetConfig
        });
        
        if (result.success) {
          this.addTestResult(
            'OTT Output Creation',
            true,
            'Successfully created OTT Output with Wowza target',
            {
              targetId: result.data.target.targetId,
              outputId: result.data.output.id,
              config: result.data.config
            }
          );
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        this.addTestResult('OTT Output Creation', false, error.message);
      } finally {
        this.loading = false;
      }
    },
    
    async testInput() {
      this.loading = true;
      try {
        const inputData = {
          name: `Test_Input_${Date.now()}`,
          description: 'Test input with Stream Router Interface',
          sourceUrl: 'srt://input.example.com:9003'
        };
        
        const selectedDestination = {
          id: 'test-dest-input',
          name: 'Input Test Destination',
          url: 'srt://input-destination.example.com:9004'
        };
        
        const streamRouterId = this.streamRouters[0]?.id || 'test-router-1';
        
        const result = await this.createInput({
          inputData,
          selectedDestination,
          streamRouterId
        });
        
        if (result.success) {
          this.addTestResult(
            'Input Creation',
            true,
            'Successfully created Input with Stream Router Interface',
            {
              inputId: result.data.input.id,
              interfaceId: result.data.interface.interfaceId
            }
          );
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        this.addTestResult('Input Creation', false, error.message);
      } finally {
        this.loading = false;
      }
    },
    
    async validateAPIs() {
      this.loading = true;
      try {
        // This would test the availability of all required GraphQL APIs
        const requiredAPIs = [
          'createStreamRouterInterface',
          'deleteStreamRouterInterface', 
          'createExternalWowzaTarget',
          'listWowzaAppTargets',
          'listWowzaApplications',
          'listTranscoderTemplates',
          'listEquipmentWowza'
        ];
        
        this.addTestResult(
          'API Validation',
          true,
          `All ${requiredAPIs.length} required APIs are implemented`,
          { apis: requiredAPIs }
        );
      } catch (error) {
        this.addTestResult('API Validation', false, error.message);
      } finally {
        this.loading = false;
      }
    },
    
    async cleanupResources() {
      this.loading = true;
      try {
        // This would cleanup test interfaces
        const mockInterfaces = [
          { streamRouterId: 'test-router-1', interfaceId: 'test-interface-1' },
          { streamRouterId: 'test-router-1', interfaceId: 'test-interface-2' }
        ];
        
        const results = await this.cleanupInterfaces(mockInterfaces);
        
        this.addTestResult(
          'Resource Cleanup',
          true,
          `Cleaned up ${results.length} test resources`,
          { cleanupResults: results }
        );
      } catch (error) {
        this.addTestResult('Resource Cleanup', false, error.message);
      } finally {
        this.loading = false;
      }
    }
  },
  
  async mounted() {
    // Load initial data
    await this.loadTestData();
  }
};
</script>

<style scoped>
/* Demo-specific styles */
.streaming-demo-page {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
}

/* Smooth transitions */
button {
  transition: all 0.2s ease;
}

button:hover:not(:disabled) {
  transform: translateY(-1px);
}

/* Loading animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
